import { z } from 'zod';

/**
 * ObraFit Module 5 · Stock control.
 *
 * `currentQty` and `belowMinimum` are derived in the service from
 * stock_movements; never persisted on stock_items.
 */

export const stockItemSchema = z.object({
  id: z.string(),
  projectId: z.string(),
  insumoId: z.string().nullable(),
  description: z.string(),
  unit: z.string(),
  minimumQty: z.number().nullable(),
  currentQty: z.number(),
  belowMinimum: z.boolean(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type StockItem = z.infer<typeof stockItemSchema>;

export const createStockItemRequestSchema = z.object({
  projectId: z.string().min(1),
  description: z.string().trim().min(1).max(200),
  unit: z.string().trim().min(1).max(20),
  insumoId: z.string().nullable().optional(),
  minimumQty: z.number().positive().nullable().optional(),
});
export type CreateStockItemRequest = z.infer<typeof createStockItemRequestSchema>;

export const updateStockItemRequestSchema = createStockItemRequestSchema.partial().omit({ projectId: true });
export type UpdateStockItemRequest = z.infer<typeof updateStockItemRequestSchema>;

export const movementDirectionSchema = z.enum(['entrada', 'saida']);
export type MovementDirection = z.infer<typeof movementDirectionSchema>;

export const stockMovementSchema = z.object({
  id: z.string(),
  stockItemId: z.string(),
  projectId: z.string(),
  direction: movementDirectionSchema,
  quantity: z.number().positive(),
  movementDate: z.string(),
  sourcePoId: z.string().nullable(),
  sourceReceiptId: z.string().nullable(),
  sourceLabel: z.string(),
  notes: z.string().nullable(),
  createdBy: z.string(),
  createdAt: z.string(),
});
export type StockMovement = z.infer<typeof stockMovementSchema>;

export const createMovementRequestSchema = z.object({
  projectId: z.string().min(1),
  stockItemId: z.string().min(1),
  direction: movementDirectionSchema,
  quantity: z.number().positive(),
  movementDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  sourcePoId: z.string().nullable().optional(),
  sourceReceiptId: z.string().nullable().optional(),
  notes: z.string().max(400).nullable().optional(),
});
export type CreateMovementRequest = z.infer<typeof createMovementRequestSchema>;

export const stockSummarySchema = z.object({
  projectId: z.string(),
  items: z.array(stockItemSchema),
  totalItems: z.number().int(),
  itemsBelowMinimum: z.number().int(),
});
export type StockSummary = z.infer<typeof stockSummarySchema>;
